import React, { Component } from "react";
import axios from "axios";
import { Toaster } from "react-hot-toast";
import TopNav from "../../components/common/TopNav";
import SideBar from "../../components/common/SideBar";
import { apiUrl } from "../../config/config";

import "./reportShared.css";
import "./reportSharedPatient.css";
import * as XLSX from "xlsx";
import { flexRender } from "@tanstack/react-table";
import DateRangePicker from "../../components/common/DateRangePicker";
import useCachedFetch from "../../utils/useCachedFetch";
import { setCache, getCache, isFresh, clearCache } from "../../utils/apiCache";
import "../../components/common/dashboard.css";

class ReportPatientsList extends Component {
  async fetchPatientsByDate(startDate, endDate) {
    // Drop anything the previous selection's lookback harvested — those rows
    // qualified for a different day and must never carry over.
    this.setState({ loading: true, error: null, extraGenerated: [] });
    try {
      // Ensure both dates are provided
      if (!startDate || !endDate) {
        const now = new Date();
        const today =
          now.getFullYear() +
          "-" +
          String(now.getMonth() + 1).padStart(2, "0") +
          "-" +
          String(now.getDate()).padStart(2, "0");
        startDate = startDate || today;
        endDate = endDate || startDate;
      }
      const detailCacheKey = `reportPatients:${startDate}:${endDate}`;
      let data = null;
      let summaryData = null;

      const detailEntry = getCache(detailCacheKey);
      if (detailEntry && isFresh({ ...detailEntry, ttlMs: 5 * 60 * 1000 })) {
        data = detailEntry.data;
        // summary has no cache — fetch alone
        const summaryRes = await axios.post(
          `${apiUrl}api/patients/getReportPatients`,
          { startdate: startDate, enddate: endDate }
        );
        summaryData = summaryRes.data;
      } else {
        // Fetch both in parallel — saves one full round-trip
        const [detailRes, summaryRes] = await Promise.all([
          axios.post(`${apiUrl}api/patients/getReportPatientsDetails_New`, {
            startdate: startDate,
            enddate: endDate,
          }),
          axios.post(`${apiUrl}api/patients/getReportPatients`, {
            startdate: startDate,
            enddate: endDate,
          }),
        ]);
        data = detailRes.data;
        summaryData = summaryRes.data;
        setCache(detailCacheKey, data, 5 * 60 * 1000);
      }
      let patients = [];
      if (data && Array.isArray(data)) {
        // Create a map to track unique patients and their sources
        const uniqueMap = new Map();

        // Process each date's data
        data.forEach((dateData) => {
          // Process created patients
          if (
            dateData.created_patients &&
            Array.isArray(dateData.created_patients)
          ) {
            dateData.created_patients.forEach((p) => {
              const key = String(p._id || p.device_id);
              if (!uniqueMap.has(key)) {
                uniqueMap.set(key, { ...p, source: "created" });
              }
            });
          }

          // Process uploaded patients
          if (
            dateData.uploaded_patients &&
            Array.isArray(dateData.uploaded_patients)
          ) {
            dateData.uploaded_patients.forEach((p) => {
              const key = String(p._id || p.device_id);
              if (!uniqueMap.has(key)) {
                uniqueMap.set(key, { ...p, source: "uploaded" });
              }
            });
          }

          // Process report shared patients
          if (
            dateData.report_shared_patients &&
            Array.isArray(dateData.report_shared_patients)
          ) {
            dateData.report_shared_patients.forEach((p) => {
              const key = String(p._id || p.device_id);
              if (!uniqueMap.has(key)) {
                uniqueMap.set(key, { ...p, source: "shared" });
              } else {
                // If patient already exists, update shared status
                const existing = uniqueMap.get(key);
                uniqueMap.set(key, {
                  ...existing,
                  ...p,
                  mailsentime: p.mailsentime || existing.mailsentime,
                  whatsappsenttime:
                    p.whatsappsenttime || existing.whatsappsenttime,
                });
              }
            });
          }

          // Process report generated patients — these can fall outside the
          // created/uploaded/shared buckets for this date (e.g. created on
          // an earlier day, report generated later), so without this pass
          // they'd be missing from the list even though they're counted in
          // the "Generated" summary total.
          //
          // Each patient the server placed in this bucket is tagged with
          // _inGeneratedBucket. The Generated filter reads that tag instead of
          // re-deriving the day from ReportGeneration on the client, because the
          // server buckets ReportGeneration with an IST -5:30 shift AND unions in
          // patients matched by ReportPublished. Any client-side re-derivation
          // drifts from the count; using the tag makes the list equal the count
          // by construction.
          if (
            dateData.report_generated_patients &&
            Array.isArray(dateData.report_generated_patients)
          ) {
            dateData.report_generated_patients.forEach((p) => {
              const key = String(p._id || p.device_id);
              if (!uniqueMap.has(key)) {
                uniqueMap.set(key, {
                  ...p,
                  source: "generated",
                  _inGeneratedBucket: true,
                });
              } else {
                const existing = uniqueMap.get(key);
                uniqueMap.set(key, {
                  ...existing,
                  ...p,
                  ReportGeneration: p.ReportGeneration || existing.ReportGeneration,
                  _inGeneratedBucket: true,
                });
              }
            });
          }
        });

        // Convert map to array
        patients = Array.from(uniqueMap.values());

          // Normalize doctor names across fetched patients to merge variants
          patients = patients.map((p) => {
            try {
              const resolved = this.normalizeDoctorName(
                (p.updatedDotorName && p.updatedDotorName !== "NA")
                  ? p.updatedDotorName
                  : p.doctorname
              );
              if (resolved) {
                p.updatedDotorName = resolved;
                p.doctorname = resolved;
              }
            } catch (e) {
              // ignore normalization errors
            }
            return p;
          });
      }
      const totalRow =
        Array.isArray(summaryData) &&
        summaryData.find((row) => row && row.date === "TOTAL");

      const normalizedSummaryTotals = {
        created_patient: Number(totalRow && totalRow.created_patient) || 0,
        uploaded: Number(totalRow && totalRow.uploaded) || 0,
        confirmed_yes: Number(totalRow && totalRow.confirmed_yes) || 0,
        report_shared: Number(totalRow && totalRow.report_shared) || 0,
        report_generated: Number(totalRow && totalRow.report_generated) || 0,
      };

      this.setState({
        patients,
        summaryTotals: normalizedSummaryTotals,
        loading: false,
      });

      // ── INTERIM FALLBACK (remove once the backend fix is deployed) ─────────
      // A patient whose report was PUBLISHED on the selected day but never
      // mailed or WhatsApp'd matches none of this endpoint's created / uploaded
      // / shared queries, so it only ever arrives inside
      // report_generated_patients. On the un-deployed backend that bucket comes
      // back empty (ReportGeneration is `type: String` in the schema but a BSON
      // Date in Mongo, so its range query matches nothing), which is why the
      // Generated card read 40 instead of 46.
      //
      // When that bucket is empty we re-query a wider window — those patients
      // were created a few days earlier, so a lookback picks them up via
      // created_at — and keep the ones whose mail / whatsapp / published day
      // lands in the selected range. Once the backend is deployed the bucket is
      // populated and this branch never runs, so it costs nothing then.
      const serverSentGeneratedBucket =
        Array.isArray(data) &&
        data.some(
          (d) =>
            d &&
            Array.isArray(d.report_generated_patients) &&
            d.report_generated_patients.length > 0
        );
      if (!serverSentGeneratedBucket) {
        this.fetchGeneratedFallback(startDate, endDate);
      } else if (this.state.extraGenerated.length) {
        this.setState({ extraGenerated: [] });
      }
    } catch (err) {
      this.setState({ error: "Failed to fetch patients", loading: false });
    }
  }

  // Shift a "YYYY-MM-DD" day string by whole days, staying in UTC so no local
  // timezone can nudge it across a boundary.
  shiftDay = (isoDay, delta) => {
    const d = new Date(`${isoDay}T00:00:00.000Z`);
    if (isNaN(d.getTime())) return isoDay;
    d.setUTCDate(d.getUTCDate() + delta);
    return d.toISOString().slice(0, 10);
  };

  // Harvest patients that belong in the Generated list for [startDate, endDate]
  // but are absent from the single-day payload. See the note in
  // fetchPatientsByDate — this exists only until the backend ships.
  fetchGeneratedFallback = async (startDate, endDate) => {
    const LOOKBACK_DAYS = 30;
    try {
      const from = this.shiftDay(startDate, -LOOKBACK_DAYS);
      const cacheKey = `reportPatientsLookback:${from}:${endDate}`;

      let wide = null;
      const cached = getCache(cacheKey);
      if (cached && isFresh({ ...cached, ttlMs: 5 * 60 * 1000 })) {
        wide = cached.data;
      } else {
        const res = await axios.post(
          `${apiUrl}api/patients/getReportPatientsDetails_New`,
          { startdate: from, enddate: endDate }
        );
        wide = res.data;
        setCache(cacheKey, wide, 5 * 60 * 1000);
      }
      if (!Array.isArray(wide)) return;

      // Flatten every bucket of the wide window into one de-duplicated map.
      const seen = new Map();
      for (const day of wide) {
        for (const list of [
          day && day.created_patients,
          day && day.uploaded_patients,
          day && day.report_shared_patients,
          day && day.report_generated_patients,
        ]) {
          if (!Array.isArray(list)) continue;
          for (const p of list) {
            const key = String(p._id || p.device_id || "");
            if (key && !seen.has(key)) seen.set(key, p);
          }
        }
      }

      // Keep only rows that qualify as Generated for the SELECTED range, and
      // only those the main payload didn't already provide — so the other
      // metric cards (Created / Uploaded / Shared / Confirmed) are untouched.
      const already = new Set(
        (this.state.patients || []).map((p) => String(p._id || p.device_id))
      );
      const extraGenerated = [];
      for (const [key, p] of seen) {
        if (already.has(key)) continue;
        const qualifies =
          this.isDayInRange(this.dayFromTextStamp(p.mailsentime), startDate, endDate) ||
          this.isDayInRange(this.dayFromTextStamp(p.whatsappsenttime), startDate, endDate) ||
          this.isPublishedInRange(p.ReportPublished, startDate, endDate);
        if (!qualifies) continue;
        try {
          const resolved = this.normalizeDoctorName(
            p.updatedDotorName && p.updatedDotorName !== "NA"
              ? p.updatedDotorName
              : p.doctorname
          );
          if (resolved) {
            p.updatedDotorName = resolved;
            p.doctorname = resolved;
          }
        } catch (e) {
          // ignore normalization errors
        }
        extraGenerated.push({ ...p, _inGeneratedBucket: true, _viaLookback: true });
      }

      // Only re-render if the selection still matches what we fetched for.
      if (this.state.startDate === startDate && this.state.endDate === endDate) {
        this.setState({ extraGenerated });
      }
    } catch (err) {
      console.error("Generated lookback failed:", err);
    }
  };

  async handleStartDateChange(e) {
    const newStartDate = e.target.value;
    const endDate = this.state.endDate;
    // If end date is before start date, set end date to start date
    if (endDate < newStartDate) {
      await this.setState({ startDate: newStartDate, endDate: newStartDate });
      this.fetchPatientsByDate(newStartDate, newStartDate);
    } else {
      await this.setState({ startDate: newStartDate });
      this.fetchPatientsByDate(newStartDate, endDate);
    }
  }

  async handleEndDateChange(e) {
    const newEndDate = e.target.value;
    const startDate = this.state.startDate;
    // If end date is before start date, set start date to end date
    if (newEndDate < startDate) {
      await this.setState({ startDate: newEndDate, endDate: newEndDate });
      this.fetchPatientsByDate(newEndDate, newEndDate);
    } else {
      await this.setState({ endDate: newEndDate });
      this.fetchPatientsByDate(startDate, newEndDate);
    }
  }

  async handleListAll() {
    const now = new Date();
    const today =
      now.getFullYear() +
      "-" +
      String(now.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(now.getDate()).padStart(2, "0");
    await this.setState({ startDate: today, endDate: today });
    this.fetchPatientsByDate(today, today);
  }

  // Helper: convert patients to CSV and trigger download
  handleGenerateCSV = (exportPatients, exportScope = "list") => {
    const patientsToExport = Array.isArray(exportPatients)
      ? exportPatients
      : this.state.patients;

    if (!patientsToExport || patientsToExport.length === 0) {
      alert("No patient data to export.");
      return;
    }

    // Define columns (header row)
    const columns = [
      "Device ID",
      "Name",
      "Customer Name",
      "IP/OP",
      "Doctor Name",
      "Duration",
      "Installation Time",
      "Hospital",
      "Created At",
      "Uploaded Date",
      "Confirmed Date",
      "Report Generated",
      "Report Shared",
      "Confirmed",
    ];

    // Helper: get customer name with fallback (same as UI)
    const getCustomerName = (p) => {
      if (p.customerName && p.customerName.trim().toUpperCase() !== "NA") {
        return p.customerName;
      }
      return (
        (p.device_info && p.device_info.customer_details) ||
        (p.device_infos && p.device_infos.customer_details) ||
        (p.subscribe_log && p.subscribe_log.customer_details) ||
        "Unknown"
      );
    };

    // Helper: normalize customer name to canonical sheet name
    const getCanonicalSheetName = (customerName) => {
      const upper = (customerName || "").toUpperCase().trim();
      
      // Map similar names to canonical sheet names
      // Group SURAKSHA and SAIKET variants under canonical sheet name "SAIKET"
      if (upper.includes("SURAKSHA")) return "SAIKET";
      if (upper.includes("SAIKET")) return "SAIKET";
      // Map Medifort variants to canonical SP MEDIFORT
      if (upper.includes("MEDIFORT")) return "SP MEDIFORT";
      if (upper.includes("DAYA")) return "DAYA";
      if (upper.includes("GOODHEALTH")) return "GOODHEALTH";
      if (upper.includes("GOOD HEALTH")) return "GOODHEALTH";
      if (upper.includes("WAFER")) return "WAFERHOME";
      if (upper.includes("HUMAN") && upper.includes("TOUCH")) return "HUMAN TOUCH";
      if (upper.includes("A R HEART") || upper.includes("AR HEART")) return "A R HEART CLINIC";
      if (upper.includes("SCTIMST")) return "SCTIMST";
      if (upper.includes("XAVIER")) return "DR RESHMI XAVIER";
      if (upper.includes("SUDHIR")) return "DR SUDHIR SETH";
      if (upper.includes("JAINNUDIN")) return "JAINNUDIN";
      if (upper.includes("JAINUDHIN")) return "JAINNUDIN";
      if (upper.includes("CHETAN SHRIRATH")) return "CHETAN";
      if (upper.includes("CHETAN")) return "CHETAN";




      
      return customerName || "Unknown";
    };

    // Group patients by canonical sheet name with fallback logic
    const groups = patientsToExport.reduce((acc, p) => {
      const rawName = getCustomerName(p);
      const canonicalKey = getCanonicalSheetName(rawName);
      if (!acc[canonicalKey]) acc[canonicalKey] = [];
      acc[canonicalKey].push(p);
      return acc;
    }, {});

    // Sort sheet keys: SURAKSHA, DAYA, GOODHEALTH, WAFERHOME, HUMAN TOUCH, A R HEART CLINIC, SCTIMST first, then others alphabetically
    // Ensure SAIKET is a priority sheet (replace previous SURAKSHA entry)
    const priorityOrder = ["SAIKET", "DAYA", "GOODHEALTH", "WAFERHOME", "HUMAN TOUCH", "A R HEART CLINIC", "SCTIMST"];
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      const aIdx = priorityOrder.indexOf(a);
      const bIdx = priorityOrder.indexOf(b);
      
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return a.localeCompare(b);
    });

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // For each customer (sorted), create a sheet
    sortedKeys.forEach((customerName) => {
      const list = groups[customerName];

      // Build rows for this sheet
      const sheetData = [columns];
      list.forEach((p) => {
        // Confirmed column logic
        let confirmedValue = "❌";
        if (typeof p.Confirmed === "string") {
          confirmedValue =
            p.Confirmed === "Yes" ? "Yes" : p.Confirmed === "No" ? "No" : "❌";
        }

        // Get customer name with fallback for the CSV cell (consistent with UI)
        const displayCustomerName = getCustomerName(p);

        sheetData.push([
          p.device_id || "-",
          p.name || "-",
          displayCustomerName,
          p.ip_op || "NA",
          this.normalizeDoctorName(
            (p.updatedDotorName && p.updatedDotorName !== "NA"
              ? p.updatedDotorName
              : p.doctorname)
          ) || "NA",
          p.exp_device_use_days
            ? p.exp_device_use_days === 24
              ? 1
              : p.exp_device_use_days === 48
              ? 2
              : p.exp_device_use_days === 72
              ? 3
              : p.exp_device_use_days
            : "NA",
          this.formatInstallationTime(p.subscribe_log),
          p.hospital || "-",
          this.formatDate(p.created_at),
          this.formatDate(p.updatedDate) || "Not Uploaded",
          this.formatDate(p.ConfirmupdatedDate),
          this.formatDate(p.ReportGeneration),
          this.formatDate(p.mailsentime || p.whatsappsenttime) || "Not Shared",
          confirmedValue,
        ]);
      });

      // Convert to worksheet
      const ws = XLSX.utils.aoa_to_sheet(sheetData);

      // Sanitize sheet name (max 31 chars, no special chars [:\/\\?*\[\]])
      let safeName = (customerName || "Sheet").toString();
      safeName = safeName.replace(/[:\\/\\?*\[\]]/g, "_").slice(0, 31);

      // Ensure unique sheet names
      let finalName = safeName;
      let suffix = 1;
      while (wb.SheetNames.includes(finalName)) {
        finalName = `${safeName}_${suffix}`.slice(0, 31);
        suffix++;
      }

      XLSX.utils.book_append_sheet(wb, ws, finalName);
    });

    // Generate filename and trigger download
    const filename = `patients_${exportScope}_${this.state.startDate || "all"}_${
      this.state.endDate || "all"
    }.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  constructor(props) {
    super(props);
    // Get today's date in IST (Asia/Kolkata) in YYYY-MM-DD format
    const today = new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Kolkata" });
    // Optional ?customer=... deep-link (set when arriving from the Report
    // Shared page with a customer selected) so the list opens pre-filtered.
    let initialCustomer = "";
    try {
      const search =
        (this.props.location && this.props.location.search) ||
        window.location.search ||
        "";
      initialCustomer = new URLSearchParams(search).get("customer") || "";
    } catch (e) {
      initialCustomer = "";
    }
    this.state = {
      patients: [],
      // Generated-list rows harvested by the interim lookback (see
      // fetchGeneratedFallback). Kept separate from `patients` on purpose so
      // only the Generated card / list / export see them — the Created,
      // Uploaded, Shared and Confirmed cards stay exactly as before.
      extraGenerated: [],
      loading: true,
      error: null,
      summaryTotals: {
        created_patient: 0,
        uploaded: 0,
        confirmed_yes: 0,
        report_shared: 0,
        report_generated: 0,
      },
      startDate:
        this.props.match &&
        this.props.match.params &&
        this.props.match.params.date
          ? this.props.match.params.date
          : today,
      endDate:
        this.props.match &&
        this.props.match.params &&
        this.props.match.params.date
          ? this.props.match.params.date
          : today,
      filterType: "shared", // default filter
      selectedCustomer: initialCustomer, // customer filter (deep-link aware)
      searchQuery: "", // search query
      showEditModal: false, // modal visibility
      editPatient: null, // patient being edited
      editFields: { customerName: "", ip_op: "", doctorname: "", duration: "", mailReport: "", whatsapp: "" }, // form fields
      editModalLoading: false, // loading for modal data
      editSaveLoading: false, // loading for save button
      confirmFields: null, // form fields for confirm modal
      confirmModalLoading: false, // loading for confirm modal
      confirmSaveLoading: false, // loading for confirm save button
      // interactive UI state
      sortBy: { id: "created_at", desc: true },
      pageIndex: 0,
      // Default to 100 rows/page so the day's full list usually fits on one
      // page without paging through 4× pages of 25.
      pageSize: 100,
      confirmButtonLoading: false, // loading state for confirm button
      showCsvMenu: false,
    };
  }

  toggleCsvMenu = () => {
    this.setState((prev) => ({ showCsvMenu: !prev.showCsvMenu }));
  };

  handleCsvExportOption = (patientsToExport, exportScope) => {
    this.setState({ showCsvMenu: false }, () => {
      this.handleGenerateCSV(patientsToExport, exportScope);
    });
  };

  handleEditClick = async (patient) => {
    this.setState({
      showEditModal: true,
      editModalLoading: true,
      editPatient: null,
    });
    // Simulate API call to fetch patient details (replace with real API if needed)
    try {
      // If you need to fetch more details, do it here
      // const res = await axios.get(...)
      // const patientDetails = res.data;
      // For now, use the passed patient
      setTimeout(() => {
        this.setState({
          editPatient: patient,
          editFields: {
            customerName: patient.customerName || "",
            ip_op: patient.ip_op || "",
            doctorname:
              this.normalizeDoctorName(
                patient.updatedDotorName && patient.updatedDotorName !== "NA"
                  ? patient.updatedDotorName
                  : patient.doctorname
              ) || "",
            duration: (() => {
              const n = Number(patient.exp_device_use_days);
              if (!n) return "";
              // Legacy: stored as days (< 24) → convert to hours so dropdown matches
              return String(n < 24 ? n * 24 : n);
            })(),
            mailReport: patient.mailReport || "",
            whatsapp: patient.whatsapp || "",
          },
          editModalLoading: false,
        });
      }, 500); // simulate delay
    } catch (err) {
      this.setState({ editModalLoading: false });
    }
  };

  handleEditFieldChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      editFields: { ...prev.editFields, [name]: value },
    }));
  };

  handleEditSave = async () => {
    this.setState({ editSaveLoading: true });
    const { editPatient, editFields, patients } = this.state;
    try {
      // Call backend API to update patient
      const response = await axios.post(`${apiUrl}api/patients/updateSharedDetails`, {
        userid: editPatient._id,
        doctorname: editFields.doctorname,
        ipop: editFields.ip_op,
        customerName: editFields.customerName,
        duration: editFields.duration,
        mailReport: editFields.mailReport,
        whatsapp: editFields.whatsapp,
      });
      // Use API-returned document if available, otherwise map fields manually
      const savedPatient = response.data && response.data.data;
      const updatedPatients = patients.map((p) => {
        if (p._id !== editPatient._id) return p;
        if (savedPatient) return { ...p, ...savedPatient };
        // Fallback: map editFields keys → patient model keys
        return {
          ...p,
          customerName: editFields.customerName,
          ip_op: editFields.ip_op,
          updatedDotorName: editFields.doctorname,
          exp_device_use_days: editFields.duration,
          mailReport: editFields.mailReport,
          whatsapp: editFields.whatsapp,
        };
      });
      this.setState({
        patients: updatedPatients,
        showEditModal: false,
        editPatient: null,
        editSaveLoading: false,
      });
    } catch (err) {
      console.error("Error saving patient edit:", err);
      this.setState({ editSaveLoading: false });
      alert("Failed to save changes. Please check the server and try again.");
    }
  };

  handleEditCancel = () => {
    this.setState({
      showEditModal: false,
      editPatient: null,
      editModalLoading: false,
      editSaveLoading: false,
    });
  };

  handleConfirmFieldChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      confirmFields: { ...prev.confirmFields, [name]: value },
    }));
  };

  handleConfirmClick = (patient) => {
    this.setState({
      showConfirmModal: true,
      selectedPatient: patient,
    });
  };

  handleConfirmSubmit = async () => {
    const { selectedPatient } = this.state;
    this.setState({ confirmSaveLoading: true });
    try {
      const confirmedBy = localStorage.getItem("verifiername") || "Verifier";
      await axios.post(`${apiUrl}api/patients/confirmBilling`, {
        userid: selectedPatient._id,
        Confirmed: "Yes",
        ConfirmedBy: confirmedBy,
      });

      const selectedId = String(selectedPatient._id);
      const updatedPatients = this.state.patients.map((p) =>
        String(p._id) === selectedId
          ? { ...p, Confirmed: "Yes", ConfirmedBy: confirmedBy }
          : p
      );

      // Clear sessionStorage cache so a page refresh fetches fresh data from server
      clearCache(`reportPatients:${this.state.startDate}:${this.state.endDate}`);

      this.setState({
        showConfirmModal: false,
        selectedPatient: null,
        confirmSaveLoading: false,
        patients: updatedPatients,
      });
    } catch (err) {
      console.error("Error confirming patient:", err);
      this.setState({ confirmSaveLoading: false });
      alert("Failed to confirm billing. Please check the server and try again.");
    }
  };

  handleConfirmCancel = () => {
    this.setState({
      showConfirmModal: false,
      selectedPatient: null,
      confirmFields: null,
    });
  };

  async componentDidMount() {
    this.fetchPatientsByDate(this.state.startDate, this.state.endDate);
    this.autoRefreshInterval = setInterval(() => {
      this.fetchPatientsByDate(this.state.startDate, this.state.endDate);
    }, 5 * 60 * 1000);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.startDate !== this.state.startDate ||
      prevState.endDate !== this.state.endDate
    ) {
      this.fetchPatientsByDate(this.state.startDate, this.state.endDate);
    }
  }

  componentWillUnmount() {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
    }
  }

  // Helper: normalize doctor name variants to a canonical form
  normalizeDoctorName = (name) => {
    if (!name) return name;
    let s = String(name).replace(/\./g, " ").replace(/\s+/g, " ").trim();
    const u = s.toUpperCase();

    // Merge common variants of Reshmi/Rashmi Xavier into a single canonical name
    if ((u.includes("RESHMI") || u.includes("RASHMI")) && u.includes("XAVIER")) {
      return "Dr Reshmi Xavier";
    }

    // Merge common variants of Jainnudin/Jainudhin into canonical "Jainnudin"
    if (
      u.includes("JAINNUDIN") ||
      u.includes("JAINUDHIN") ||
      u.includes("JAINUDDIN") ||
      u.includes("JAINUDIN") ||
      u.includes("JAIN NUDIN")
    ) {
      return "Jainnudin";
    }

    return s;
  };

  // Helper: short date for table cells — "Apr 17 17:35"
  formatShortDate = (dateValue) => {
    if (!dateValue) return null;
    try {
      const d = new Date(dateValue);
      if (isNaN(d.getTime())) return null;
      const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const pad = (n) => String(n).padStart(2, "0");
      return `${months[d.getMonth()]} ${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch (e) {
      return null;
    }
  };

  // Helper to format date in local time
  formatDate = (dateValue) => {
    if (!dateValue) return "❌";

    // Convert to date object
    let d;
    try {
      d = new Date(dateValue);
      if (isNaN(d.getTime())) return dateValue;
    } catch (e) {
      return dateValue;
    }

    // Manual date formatting without using any library
    function addLeadingZero(num) {
      return num < 10 ? "0" + num : num;
    }

    const year = d.getFullYear();
    const month = addLeadingZero(d.getMonth() + 1);
    const day = addLeadingZero(d.getDate());
    const hours = addLeadingZero(d.getHours());
    const minutes = addLeadingZero(d.getMinutes());
    const seconds = addLeadingZero(d.getSeconds());

    return (
      year +
      "-" +
      month +
      "-" +
      day +
      " " +
      hours +
      ":" +
      minutes +
      ":" +
      seconds
    );
  };

  // Helper: format installation time from subscribe_log
  formatInstallationTime = (subscribe_log) => {
    if (!subscribe_log || !subscribe_log.date_sub) return "NA";
    try {
      // Parse date_sub as UTC, then add 5:30 hours
      const [datePart, timePart] = subscribe_log.date_sub.split(" ");
      if (!datePart || !timePart) return "NA";
      // date_sub format: "YYYY-MM-DD HH:mm:ss.SSSSSS" or "YYYY-MM-DD HH:mm:ss.SSS"
      const [year, month, day] = datePart.split("-").map(Number);
      const [hour, minute, secondRaw] = timePart.split(":");
      let second = 0, ms = 0;
      if (secondRaw.includes(".")) {
        const [sec, msStr] = secondRaw.split(".");
        second = Number(sec);
        // If msStr is 6 digits (microseconds), convert to ms
        ms = msStr.length === 6 ? Math.round(Number(msStr) / 1000) : Number(msStr);
      } else {
        second = Number(secondRaw);
        ms = 0;
      }
      // Create UTC date
      const d = new Date(Date.UTC(year, month - 1, day, Number(hour), Number(minute), second, ms));
      // Add 5.5 hours (19800000 ms)
      d.setTime(d.getTime() + 19800000);
      // Format as YYYY-MM-DD HH:mm:ss
      const pad = (n) => String(n).padStart(2, "0");
      return (
        d.getFullYear() +
        "-" + pad(d.getMonth() + 1) +
        "-" + pad(d.getDate()) +
        " " + pad(d.getHours()) +
        ":" + pad(d.getMinutes()) +
        ":" + pad(d.getSeconds())
      );
    } catch (e) {
      return "NA";
    }
  };

  // Helper: pick relevant date field based on current filter
  getRelevantDateForFilter = (filterType, patient) => {
    if (filterType === "created") return patient.created_at;
    if (filterType === "uploaded") return patient.updatedDate;
    if (filterType === "confirmed" || filterType === "only_confirmed")
      return patient.ConfirmupdatedDate;
    if (filterType === "shared")
      return patient.mailsentime || patient.whatsappsenttime;
    if (filterType === "report_generated") return patient.ReportGeneration;
    return (
      patient.updatedDate || patient.created_at || patient.ReportGeneration
    );
  };

  // Helper: check if a date falls within the date range
  // mode "shiftBack" is for fields written as IST wall-clock time but stored
  // tagged as UTC (updatedDate / ReportGeneration) — matches the backend's
  // toISTDayFromUpdatedOffset (subtract 5.5h before reading the calendar day).
  // Using the standard +5:30 IST boundary here would bucket these fields
  // into the wrong day and cause the table to disagree with the summary count.
  isDateInRange = (dateValue, startDate, endDate, mode = "ist") => {
    if (!dateValue || !startDate || !endDate) return false;

    try {
      const patientDate = new Date(dateValue);
      if (isNaN(patientDate.getTime())) return false;

      if (mode === "shiftBack") {
        const shifted = new Date(patientDate.getTime() - 5.5 * 60 * 60 * 1000);
        const start = new Date(startDate + "T00:00:00.000Z");
        const end = new Date(endDate + "T23:59:59.999Z");
        return shifted >= start && shifted <= end;
      }

      // mode "utc" reads the value's own UTC calendar day with no shift. Used for
      // ReportGeneration, which the backend (getReportPatients / _New) buckets by
      // plain UTC day (new Date(ReportGeneration).toISOString().slice(0,10)) — so
      // the list must match, otherwise it disagrees with the Generated card.
      if (mode === "utc") {
        const start = new Date(startDate + "T00:00:00.000Z");
        const end = new Date(endDate + "T23:59:59.999Z");
        return patientDate >= start && patientDate <= end;
      }

      // Use IST boundaries (+05:30) so dates align with IST day, not UTC day
      const start = new Date(startDate + "T00:00:00+05:30");
      const end = new Date(endDate + "T23:59:59+05:30");

      return patientDate >= start && patientDate <= end;
    } catch (e) {
      return false;
    }
  };

  // Helper: is a ReportPublished value ("DD/MM/YYYY HH:mm:ss" text) within the
  // selected day range? Used so the Generated list includes reports PUBLISHED
  // on the selected day even when they were generated on a different day.
  isPublishedInRange = (published, startDate, endDate) => {
    if (!published || typeof published !== "string" || !startDate || !endDate)
      return false;
    const m = published.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (!m) return false;
    const day = `${m[3]}-${m[2]}-${m[1]}`; // -> YYYY-MM-DD
    return day >= startDate && day <= endDate;
  };

  // Helper: pull the calendar day out of a "YYYY-MM-DD HH:mm:ss" text stamp
  // (mailsentime / whatsappsenttime). These are stored as plain text with the
  // day already in local terms, so we slice the day off rather than going
  // through `new Date(...)` — that would reinterpret the string in the
  // browser's timezone and can shift it to the adjacent day. Mirrors the
  // server's getDayFromStr.
  dayFromTextStamp = (value) => {
    if (!value || typeof value !== "string") return null;
    const day = value.trim().split(" ")[0];
    return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
  };

  // Helper: plain string comparison of YYYY-MM-DD days (lexicographic order
  // matches chronological order for this format).
  isDayInRange = (day, startDate, endDate) => {
    if (!day || !startDate || !endDate) return false;
    return day >= startDate && day <= endDate;
  };

  // Helper: derive YYYY-MM key from a date-like value
  getMonthKey = (dateValue) => {
    if (!dateValue) return "Unknown";
    try {
      const d = new Date(dateValue);
      if (isNaN(d.getTime())) return "Unknown";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      return `${y}-${m}`;
    } catch (e) {
      return "Unknown";
    }
  };

  // Helper: pretty month label like "May 2025" from YYYY-MM
  getMonthLabel = (monthKey) => {
    if (!monthKey || monthKey === "Unknown") return "Unknown";
    const [y, m] = monthKey.split("-");
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const idx = Math.max(0, Math.min(11, Number(m) - 1));
    return `${monthNames[idx]} ${y}`;
  };

  render() {
    const {
      patients,
      loading,
      error,
      filterType,
      selectedCustomer,
      searchQuery,
      startDate,
      endDate,
    } = this.state;

    const menus = [
      { title: "Verifier Home", url: "/verifier-home" },
      { title: "Patient list", url: "/patient-list" },
      { title: "Labled Patients", url: "/labled-patients" },
      { title: "Verified", url: "/verification-completed" },
    ];
    if (localStorage.getItem("verifiertype") === "8") {
      menus.splice(2, 0, { title: "Patient-hold", url: "/patient-hold" });
    }
    if (localStorage.getItem("AdminUser") === "8") {
      menus.splice(3, 0, { title: "Device Details", url: "/device-details" });
      menus.splice(4, 0, { title: "Battery Issue", url: "/battery-issue" });
    }

    if (localStorage.getItem("verifiertype") === "8") {
      menus.splice(4, 0, { title: "Support Home", url: "/support-home" });
    }
    if (localStorage.getItem("verifiertype") === "8") {
      menus.splice(5, 0, { title: "QC Home", url: "/qc_home" });
    }
    if (localStorage.getItem("AdminUser") === "8") {
      menus.splice(6, 0, { title: "Report Shared", url: "/report-shared" });
    }
    if (localStorage.getItem("AdminUser") === "8") {
      menus.splice(6, 0, {
        title: "Supported Person",
        url: "/supported-persons",
      });
    }

    if (
      localStorage.getItem("verifierid") === "5d6df589c3d92110fccfde7c" ||
      localStorage.getItem("verifierid") === "688d9c64cebbc60f077de5de" ||
      localStorage.getItem("verifierid") === "688d9d4acebbc60f077de5e2"
    ) {
      menus.splice(3, 0, { title: "Device Details", url: "/device-details" });
      menus.splice(4, 0, { title: "Battery Issue", url: "/battery-issue" });
    }

    // 🔹 Filter logic
    const normalizeCustomerValue = (value) =>
      value ? String(value).trim().toLowerCase() : "";

    // Does a patient belong to the selected customer? Uses the same name
    // fallback chain as the Report Shared dropdown (customerName →
    // device_info / device_infos / subscribe_log.customer_details) so the
    // metric cards AND the table restrict to that customer. Empty selection
    // (no customer chosen) matches everyone.
    const customerMatch = (p) => {
      if (!selectedCustomer) return true;
      const name =
        p.customerName && String(p.customerName).trim().toUpperCase() !== "NA"
          ? p.customerName
          : (p.device_info && p.device_info.customer_details) ||
            (p.device_infos && p.device_infos.customer_details) ||
            (p.subscribe_log && p.subscribe_log.customer_details) ||
            "";
      return (
        normalizeCustomerValue(name) === normalizeCustomerValue(selectedCustomer)
      );
    };

    const isSingleDay = startDate && endDate && startDate === endDate;

    // Generated = report went out or was published on the selected day:
    // mailsentime OR whatsappsenttime OR ReportPublished falls in range.
    // Same rule the server uses for the Generated count, so the list and the
    // number always agree. The _inGeneratedBucket arm accepts rows the server
    // already bucketed (present once the updated backend is deployed); the date
    // arms make this work against the current backend too, so the list can
    // never come back empty. Defined once here and reused by the table filter,
    // the metric card and the CSV export.
    const isGenerated = (p) =>
      p._inGeneratedBucket === true ||
      this.isDayInRange(this.dayFromTextStamp(p.mailsentime), startDate, endDate) ||
      this.isDayInRange(this.dayFromTextStamp(p.whatsappsenttime), startDate, endDate) ||
      this.isPublishedInRange(p.ReportPublished, startDate, endDate);

    // The pool the Generated rule draws from: the day's payload plus any rows
    // the interim lookback recovered. Every other metric keeps using `patients`
    // alone, so nothing else on the page shifts.
    const extraGenerated = this.state.extraGenerated || [];
    const generatedSource = extraGenerated.length
      ? [...patients, ...extraGenerated]
      : patients;

    // Generated is the only filter that reads the wider pool.
    let filteredPatients =
      filterType === "report_generated" ? generatedSource : patients;

    // When only confirmed filter is selected, show only confirmed patients
    if (filterType === "only_confirmed") {
      filteredPatients = patients.filter((p) => {
        if (p.Confirmed !== "Yes") return false;
        const relevantDate = this.getRelevantDateForFilter(filterType, p);
        return this.isDateInRange(relevantDate, startDate, endDate);
      });
    } else if (filterType === "report_generated") {
      filteredPatients = generatedSource.filter(isGenerated);
    } else {
      filteredPatients = patients.filter((p) => {
        const dateMode = filterType === "uploaded" ? "shiftBack" : "ist";
        const relevantDate = this.getRelevantDateForFilter(filterType, p);
        return this.isDateInRange(relevantDate, startDate, endDate, dateMode);
      });
    }

    // 🔹 Always sort using created_at (newest first)
    filteredPatients = (filteredPatients || []).slice().sort((a, b) => {
      const aTime = a && a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b && b.created_at ? new Date(b.created_at).getTime() : 0;
      return (isNaN(bTime) ? 0 : bTime) - (isNaN(aTime) ? 0 : aTime);
    });

    // Apply search and customer filtering to the already date-filtered patients
    let finalFilteredPatients = filteredPatients.filter((p) => {
      const searchQuery = (this.state.searchQuery || "").toLowerCase();
      const values = [p.device_id, p.name, p.customerName].map((val) =>
        typeof val === "string"
          ? val.toLowerCase()
          : val
          ? String(val).toLowerCase()
          : ""
      );

      return (
        customerMatch(p) &&
        (!searchQuery || values.some((val) => val.includes(searchQuery)))
      );
    });

    // Sorting
    const { sortBy } = this.state;
    const getSortValue = (row, id) => {
      if (id === "_index") return 0;
      if (id === "created_at") return new Date(row.created_at || 0).getTime();
      if (id === "updatedDate") return new Date(row.updatedDate || 0).getTime();
      if (id === "ConfirmupdatedDate")
        return new Date(row.ConfirmupdatedDate || 0).getTime();
      if (id === "ReportGeneration")
        return new Date(row.ReportGeneration || 0).getTime();
      return (row[id] || "").toString().toLowerCase();
    };
    finalFilteredPatients = finalFilteredPatients.slice().sort((a, b) => {
      const av = getSortValue(a, sortBy.id);
      const bv = getSortValue(b, sortBy.id);
      if (av < bv) return sortBy.desc ? 1 : -1;
      if (av > bv) return sortBy.desc ? -1 : 1;
      return 0;
    });

    // Created / Uploaded / Shared: count from patients array (matches what the
    // table shows). customerMatch restricts each card to the selected customer
    // (when one is chosen) so the cards agree with the filtered list.
    const totalOverviewCount = patients.filter(p => customerMatch(p) && this.isDateInRange(p.created_at, startDate, endDate)).length;
    const uploadedOverviewCount = patients.filter(p => customerMatch(p) && this.isDateInRange(p.updatedDate, startDate, endDate, "shiftBack")).length;
    const sharedPatients = patients.filter(p => customerMatch(p) && this.isDateInRange(p.mailsentime || p.whatsappsenttime, startDate, endDate));
    const sharedOverviewCount = sharedPatients.length;
    // Total monitoring days for shared patients (exp_device_use_days stored in hours, divide by 24)
    const sharedDaysCount = sharedPatients.reduce((sum, p) => sum + Math.round((Number(p.exp_device_use_days) || 0) / 24), 0);
    // Generated card reuses the single isGenerated predicate defined above over
    // the same pool the list renders, so the number on the card is by definition
    // the number of rows listed.
    const generatedPatients = generatedSource.filter(p => customerMatch(p) && isGenerated(p));
    const generatedOverviewCount = generatedPatients.length;
    const confirmedOverviewCount = patients.filter(p => customerMatch(p) && p.Confirmed === "Yes").length;

    // Pagination
    const { pageIndex, pageSize } = this.state;
    const total = finalFilteredPatients.length;
    const pageCount = Math.ceil(total / pageSize) || 1;
    const currentPage = Math.min(pageIndex, pageCount - 1);
    const pageRows = finalFilteredPatients.slice(
      currentPage * pageSize,
      currentPage * pageSize + pageSize
    );

    // For single-day view, group by month (optional grouping)
    let groupsMap = {};
    let monthKeys = [];
    if (isSingleDay) {
      groupsMap = {};
      finalFilteredPatients.forEach((p) => {
        const relDate = this.getRelevantDateForFilter(filterType, p);
        const key = this.getMonthKey(relDate);
        if (!groupsMap[key]) groupsMap[key] = [];
        groupsMap[key].push(p);
      });
      monthKeys = Object.keys(groupsMap).sort((a, b) => {
        if (a === "Unknown") return 1;
        if (b === "Unknown") return -1;
        return a.localeCompare(b);
      });
    }

    return (
      <div className="wrapper theme-6-active box-layout pimary-color-green">
        <Toaster position="top-right" reverseOrder={false} />
        <TopNav />
        <SideBar menus={menus} />
        <div className="page-wrapper report-patients-page">
          <div className="container-fluid">
            <div className="panel panel-default card-view">
              {/* ── Header ── */}
              <div className="panel-heading report-shared-heading">
                <div className="rpl-header-row">
                  <div>
                    <div className="rpl-page-title">Patient Report List</div>
                    <div className="rpl-date-range">
                      {startDate === endDate
                        ? startDate
                        : `${startDate} — ${endDate}`}
                    </div>
                  </div>
                  <div style={{ position: "relative" }}>
                    <button className="rs-view-btn rpl-csv-btn" onClick={this.toggleCsvMenu}>
                      Export CSV
                    </button>
                    {this.state.showCsvMenu && (
                      <div className="rpl-csv-menu">
                        {/*
                          Each export option maps 1:1 to a metric card so the
                          row count matches what staff already see at the top:
                            "Confirmed" card  → "Confirmed" export (Confirmed=Yes
                              AND ConfirmupdatedDate in range)
                            "Generated" card  → "Generated" export
                            "Report Shared"   → "Shared" export
                            "Uploaded"        → "Uploaded" export
                          "Shared AND Confirmed" stays as the strict-intersection
                          option (rare — both events on the same day) and
                          "Shared OR Confirmed" is the union for "any activity".
                        */}
                        {(() => {
                          const all = this.state.patients || [];
                          const sharedSet = new Set(
                            all
                              .filter((p) =>
                                this.isDateInRange(
                                  p.mailsentime || p.whatsappsenttime,
                                  startDate,
                                  endDate
                                )
                              )
                              .map((p) => String(p._id || p.device_id))
                          );
                          const confirmedSet = new Set(
                            all
                              .filter(
                                (p) =>
                                  p.Confirmed === "Yes" &&
                                  this.isDateInRange(
                                    p.ConfirmupdatedDate,
                                    startDate,
                                    endDate
                                  )
                              )
                              .map((p) => String(p._id || p.device_id))
                          );
                          // Generated export = the exact rows the card counts and
                          // the table lists (generatedPatients), including any
                          // recovered by the interim lookback.
                          const uploadedSet = new Set(
                            all
                              .filter((p) =>
                                this.isDateInRange(
                                  p.updatedDate,
                                  startDate,
                                  endDate,
                                  "shiftBack"
                                )
                              )
                              .map((p) => String(p._id || p.device_id))
                          );

                          const inSet = (set) =>
                            all.filter((p) =>
                              set.has(String(p._id || p.device_id))
                            );
                          const sharedRows = inSet(sharedSet);
                          const confirmedRows = inSet(confirmedSet);
                          const generatedRows = generatedPatients;
                          const uploadedRows = inSet(uploadedSet);
                          const sharedAndConfirmed = inSet(
                            new Set(
                              [...sharedSet].filter((id) =>
                                confirmedSet.has(id)
                              )
                            )
                          );
                          const sharedOrConfirmed = inSet(
                            new Set([...sharedSet, ...confirmedSet])
                          );

                          const itemBtn = (label, count, rows, scope) => (
                            <button
                              key={scope}
                              className="rpl-csv-item"
                              onClick={() =>
                                this.handleCsvExportOption(rows, scope)
                              }
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 12,
                              }}
                            >
                              <span>{label}</span>
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#888",
                                  background: "#f4f4f4",
                                  borderRadius: 8,
                                  padding: "1px 7px",
                                }}
                              >
                                {count}
                              </span>
                            </button>
                          );

                          return (
                            <>
                              {itemBtn(
                                "Confirmed (this date)",
                                confirmedRows.length,
                                confirmedRows,
                                "confirmed"
                              )}
                              {itemBtn(
                                "Report Shared (this date)",
                                sharedRows.length,
                                sharedRows,
                                "shared"
                              )}
                              {itemBtn(
                                "Report Generated (this date)",
                                generatedRows.length,
                                generatedRows,
                                "generated"
                              )}
                              {itemBtn(
                                "Uploaded (this date)",
                                uploadedRows.length,
                                uploadedRows,
                                "uploaded"
                              )}
                              <div
                                style={{
                                  height: 1,
                                  background: "#eee",
                                  margin: "4px 0",
                                }}
                              />
                              {itemBtn(
                                "Shared AND Confirmed (intersection)",
                                sharedAndConfirmed.length,
                                sharedAndConfirmed,
                                "shared_confirmed"
                              )}
                              {itemBtn(
                                "Shared OR Confirmed (union)",
                                sharedOrConfirmed.length,
                                sharedOrConfirmed,
                                "shared_or_confirmed"
                              )}
                              <div
                                style={{
                                  height: 1,
                                  background: "#eee",
                                  margin: "4px 0",
                                }}
                              />
                              {itemBtn(
                                "Filtered List (visible)",
                                finalFilteredPatients.length,
                                finalFilteredPatients,
                                "filtered"
                              )}
                              {itemBtn(
                                "Full List (all fetched)",
                                all.length,
                                all,
                                "full"
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Metric cards ── */}
                <div className="rpl-metric-grid">
                  <div
                    className={`rpl-metric-card${filterType === "created" ? " rpl-card-active" : ""}`}
                    onClick={() => this.setState({ filterType: "created" })}
                  >
                    <span className="rpl-metric-label">Created</span>
                    <span className="rpl-metric-value rpl-color-blue">{totalOverviewCount}</span>
                  </div>
                  <div
                    className={`rpl-metric-card${filterType === "uploaded" ? " rpl-card-active" : ""}`}
                    onClick={() => this.setState({ filterType: "uploaded" })}
                  >
                    <span className="rpl-metric-label">Uploaded</span>
                    <span className="rpl-metric-value rpl-color-orange">{uploadedOverviewCount}</span>
                  </div>
                  <div
                    className={`rpl-metric-card${filterType === "shared" ? " rpl-card-active" : ""}`}
                    onClick={() => this.setState({ filterType: "shared" })}
                  >
                    <span className="rpl-metric-label">Report Shared</span>
                    <div className="rs-shared-counts">
                      <span className="rpl-metric-value rpl-color-green">{sharedOverviewCount}</span>
                      <span className="rs-shared-sep">/</span>
                      <span className="rpl-metric-value rpl-color-green">{sharedDaysCount}</span>
                    </div>
                    <span className="rs-shared-days-label">days</span>
                  </div>
                  <div
                    className={`rpl-metric-card${filterType === "report_generated" ? " rpl-card-active" : ""}`}
                    onClick={() => this.setState({ filterType: "report_generated" })}
                  >
                    <span className="rpl-metric-label">Generated</span>
                    <span className="rpl-metric-value rpl-color-teal">{generatedOverviewCount}</span>
                  </div>
                  <div
                    className={`rpl-metric-card${filterType === "only_confirmed" ? " rpl-card-active" : ""}`}
                    onClick={() => this.setState({ filterType: "only_confirmed" })}
                  >
                    <span className="rpl-metric-label">Confirmed</span>
                    <span className="rpl-metric-value rpl-color-purple">{confirmedOverviewCount}</span>
                  </div>
                </div>

                {/* ── Filter bar ── */}
                <div className="rpl-filter-bar">
                  <select
                    className="rpl-select"
                    value={filterType}
                    onChange={(e) => this.setState({ filterType: e.target.value })}
                  >
                    <option value="created">Created</option>
                    <option value="uploaded">Uploaded</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="shared">Report Shared</option>
                    <option value="report_generated">Report Generated</option>
                    <option value="only_confirmed">Confirmed Billing</option>
                  </select>

                  <DateRangePicker
                    startDate={this.state.startDate}
                    endDate={this.state.endDate}
                    onChange={({ startDate, endDate }) => {
                      this.setState({ startDate, endDate });
                    }}
                  />

                  <select
                    className="rpl-select"
                    style={{ minWidth: "180px" }}
                    value={this.state.selectedCustomer || ""}
                    onChange={(e) => this.setState({ selectedCustomer: e.target.value })}
                  >
                    <option value="">All Customers</option>
                    {Array.from(
                      new Set(patients.map((p) => p.customerName).filter(Boolean).filter(v => v.trim().toUpperCase() !== "NA"))
                    ).sort().map((name, idx) => (
                      <option key={idx} value={name}>{name}</option>
                    ))}
                  </select>

                  <input
                    type="text"
                    className="rpl-search"
                    placeholder="Search name, device ID..."
                    value={this.state.searchQuery || ""}
                    onChange={(e) => this.setState({ searchQuery: e.target.value })}
                  />
                </div>
              </div>

              {/* ── Loading / Error ── */}
              {loading && (
                <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
                  Loading...
                </div>
              )}
              {error && (
                <div style={{ padding: "12px 16px", color: "#ff6b6b" }}>{error}</div>
              )}

              {/* ── Table ── */}
              <div className="panel-body">
                <div className="rs-table-wrapper">
                  <table className="rs-table rpl-table rpl-fixed-table">
                    <colgroup>
                      <col style={{width:"28px"}} />
                      <col style={{width:"100px"}} />
                      <col style={{width:"88px"}} />
                      <col style={{width:"58px"}} />
                      <col style={{width:"82px"}} />
                      <col style={{width:"36px"}} />
                      <col style={{width:"78px"}} />
                      <col style={{width:"88px"}} />
                      <col style={{width:"88px"}} />
                      <col style={{width:"82px"}} />
                      <col style={{width:"82px"}} />
                      <col style={{width:"76px"}} />
                    </colgroup>
              <thead>
                <tr>
                  <th>#</th>
                  <th onClick={() => this.setState({ sortBy: { id: "device_id", desc: false } })}>Device / Patient</th>
                  <th onClick={() => this.setState({ sortBy: { id: "customerName", desc: false } })}>Customer</th>
                  <th>IP/OP</th>
                  <th onClick={() => this.setState({ sortBy: { id: "doctorname", desc: false } })}>Doctor</th>
                  <th>Days</th>
                  <th>Hospital</th>
                  <th onClick={() => this.setState({ sortBy: { id: "created_at", desc: true } })}>Created / Uploaded</th>
                  <th onClick={() => this.setState({ sortBy: { id: "ConfirmupdatedDate", desc: true } })}>Confirmed / Generated</th>
                  <th>Email Delivery</th>
                  <th>WA Delivery</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((p, idx) => {
                  // Debug: log patient object for first 3 rows
                  if (idx < 3) {
                    // console.log('Patient row', idx, p);
                    if (p.subscribe_log) {
                      // console.log('subscribe_log:', p.subscribe_log);
                    } else {
                      // console.log('No subscribe_log for patient', p.device_id || p._id);
                    }
                  }
                  return (
                    <tr
                      key={`patient-${idx}`}
                      className={p.Confirmed === "Yes" ? "rpl-row-confirmed" : ""}
                    >
                      <td>{currentPage * pageSize + idx + 1}</td>
                      <td>
                        <div className="rpl-device-id">{p.device_id || "-"}</div>
                        <div className="rpl-patient-name">{p.name || "-"}</div>
                      </td>
                      <td>{
                        p.customerName && p.customerName.trim().toUpperCase() !== "NA"
                          ? p.customerName
                          : (
                              (p.device_info && p.device_info.customer_details) ||
                              (p.device_infos && p.device_infos.customer_details) ||
                              (p.subscribe_log && p.subscribe_log.customer_details) ||
                              "-"
                            )
                      }</td>
                      <td>{p.ip_op || "NA"}</td>
                      <td className="rpl-td-doctor">
                        {this.normalizeDoctorName(
                          (p.updatedDotorName && p.updatedDotorName !== "NA")
                            ? p.updatedDotorName
                            : p.doctorname
                        ) || "NA"}
                      </td>
                      <td>{(() => { const n = Number(p.exp_device_use_days); if (!n) return <span className="rpl-na">—</span>; const map = {24:"1d",48:"2d",72:"3d",96:"4d",120:"5d",144:"6d",168:"7d",240:"10d",336:"14d",744:"31d"}; if (map[n]) return map[n]; if (n < 24) return `${n}d`; return `${Math.round(n/24)}d`; })()}</td>
                      <td className="rpl-td-hospital">{p.hospital || <span className="rpl-na">—</span>}</td>
                      <td className="rpl-td-date">
                        <div><span className="rpl-col-sublabel">C </span>{this.formatShortDate(p.created_at) || <span className="rpl-na">—</span>}</div>
                        <div><span className="rpl-col-sublabel">U </span>{this.formatShortDate(p.updatedDate) || <span className="rpl-na">—</span>}</div>
                      </td>
                      <td className="rpl-td-date">
                        <div><span className="rpl-col-sublabel">C </span>{this.formatShortDate(p.ConfirmupdatedDate) || <span className="rpl-na">—</span>}</div>
                        <div><span className="rpl-col-sublabel">G </span>{this.formatShortDate(p.ReportGeneration) || <span className="rpl-na">—</span>}</div>
                      </td>
                      <td className="rpl-td-delivery">
                        {p.mailsentime ? (
                          <div>
                            <span className="rpl-delivery-badge rpl-delivery-sent">Sent</span>
                            <div className="rpl-delivery-time">{this.formatShortDate(p.mailsentime)}</div>
                            {p.mailReport && <div className="rpl-delivery-to">{p.mailReport}</div>}
                          </div>
                        ) : (
                          <span className="rpl-delivery-badge rpl-delivery-none">Not Sent</span>
                        )}
                      </td>
                      <td className="rpl-td-delivery">
                        {p.whatsappsenttime ? (
                          <div>
                            <span className="rpl-delivery-badge rpl-delivery-wa">Sent</span>
                            <div className="rpl-delivery-time">{this.formatShortDate(p.whatsappsenttime)}</div>
                            {p.whatsapp && <div className="rpl-delivery-to">{p.whatsapp}</div>}
                          </div>
                        ) : (
                          <span className="rpl-delivery-badge rpl-delivery-none">Not Sent</span>
                        )}
                      </td>
                      <td className="rpl-td-actions">
                        <button
                          className="rpl-action-btn rpl-action-edit"
                          onClick={() => this.handleEditClick(p)}
                        >
                          Edit
                        </button>
                        {p.Confirmed === "Yes" ? (
                          <div className="rpl-confirmed-info">
                            <span className="rpl-delivery-badge rpl-delivery-sent">Confirmed</span>
                            {p.ConfirmedBy && <div className="rpl-confirmed-by">{p.ConfirmedBy}</div>}
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="rpl-action-btn rpl-action-confirm"
                            onClick={() => this.handleConfirmClick(p)}
                          >
                            Confirm
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div className="rpl-pagination">
                <span className="rpl-page-info">
                  Showing {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, total)} of {total}
                </span>
                <div className="rpl-page-controls">
                  <button className="rpl-page-btn" disabled={currentPage === 0} onClick={() => this.setState({ pageIndex: 0 })}>«</button>
                  <button className="rpl-page-btn" disabled={currentPage === 0} onClick={() => this.setState({ pageIndex: currentPage - 1 })}>‹</button>
                  <span className="rpl-page-num">Page {currentPage + 1} / {pageCount}</span>
                  <button className="rpl-page-btn" disabled={currentPage >= pageCount - 1} onClick={() => this.setState({ pageIndex: currentPage + 1 })}>›</button>
                  <button className="rpl-page-btn" disabled={currentPage >= pageCount - 1} onClick={() => this.setState({ pageIndex: pageCount - 1 })}>»</button>
                  <select className="rpl-select" value={pageSize} onChange={(e) => this.setState({ pageSize: Number(e.target.value), pageIndex: 0 })}>
                    {[10, 25, 50, 100].map((s) => <option key={s} value={s}>{s}/page</option>)}
                  </select>
                </div>
            </div>
          </div>

          {/* Edit Modal */}
            {this.state.showEditModal && (
              <div className="rpl-modal-overlay">
                <div className="rpl-modal">
                  <h5 className="rpl-modal-title">Edit Patient Details</h5>
                  {this.state.editModalLoading ? (
                    <div style={{ textAlign: "center", padding: "30px 0" }}>
                      <span
                        className="spinner-border"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span style={{ marginLeft: 10 }}>Loading...</span>
                    </div>
                  ) : (
                    <React.Fragment>
                      <div className="rpl-modal-grid">
                        <div className="rpl-modal-field">
                          <label>Customer Name</label>
                          <select name="customerName" className="form-control" value={this.state.editFields.customerName || ""} onChange={this.handleEditFieldChange}>
                            <option value="">Select Customer</option>
                            {Array.from(
                              new Set(this.state.patients.map((p) => p.customerName).filter(Boolean).filter(v => v.trim().toUpperCase() !== "NA"))
                            ).sort().map((name, idx) => (
                              <option key={idx} value={name}>{name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="rpl-modal-field">
                          <label>IP / OP (Patient ID)</label>
                          <input name="ip_op" className="form-control" placeholder="e.g. PR1234567 or OP" value={this.state.editFields.ip_op} onChange={this.handleEditFieldChange} />
                        </div>
                        <div className="rpl-modal-field">
                          <label>Doctor Name</label>
                          <select name="doctorname" className="form-control" value={this.state.editFields.doctorname} onChange={this.handleEditFieldChange}>
                            <option value="">Select Doctor</option>
                            {[...new Set(
                              this.state.patients
                                .map(p => this.normalizeDoctorName(
                                  (p.updatedDotorName && p.updatedDotorName !== "NA") ? p.updatedDotorName : p.doctorname
                                ))
                                .filter(Boolean)
                            )].sort().map(name => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                            {/* Keep current value if not in list */}
                            {this.state.editFields.doctorname &&
                              !this.state.patients.some(p =>
                                this.normalizeDoctorName((p.updatedDotorName && p.updatedDotorName !== "NA") ? p.updatedDotorName : p.doctorname) === this.state.editFields.doctorname
                              ) && (
                              <option value={this.state.editFields.doctorname}>{this.state.editFields.doctorname}</option>
                            )}
                          </select>
                        </div>
                        <div className="rpl-modal-field">
                          <label>Duration</label>
                          <select name="duration" className="form-control" value={this.state.editFields.duration || ""} onChange={this.handleEditFieldChange}>
                            <option value="">Select</option>
                            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                              <option key={d} value={String(d * 24)}>{d} day{d > 1 ? "s" : ""}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="rpl-modal-section-title">Report Delivery</div>
                      <div className="rpl-modal-grid">
                        <div className="rpl-modal-field">
                          <label>Email Address</label>
                          <input name="mailReport" className="form-control" type="email" placeholder="recipient@email.com" value={this.state.editFields.mailReport} onChange={this.handleEditFieldChange} />
                        </div>
                        <div className="rpl-modal-field">
                          <label>WhatsApp Number</label>
                          <input name="whatsapp" className="form-control" type="tel" placeholder="+91XXXXXXXXXX" value={this.state.editFields.whatsapp} onChange={this.handleEditFieldChange} />
                        </div>
                      </div>
                      {this.state.editPatient && (
                        <div className="rpl-modal-delivery-status">
                          <div className={`rpl-ds-item ${this.state.editPatient.mailsentime ? "rpl-ds-sent" : "rpl-ds-pending"}`}>
                            <span className="rpl-ds-icon">✉</span>
                            <span className="rpl-ds-label">Email</span>
                            <span className="rpl-ds-val">{this.state.editPatient.mailsentime ? this.formatShortDate(this.state.editPatient.mailsentime) : "Not sent"}</span>
                          </div>
                          <div className={`rpl-ds-item ${this.state.editPatient.whatsappsenttime ? "rpl-ds-wa" : "rpl-ds-pending"}`}>
                            <span className="rpl-ds-icon">📱</span>
                            <span className="rpl-ds-label">WhatsApp</span>
                            <span className="rpl-ds-val">{this.state.editPatient.whatsappsenttime ? this.formatShortDate(this.state.editPatient.whatsappsenttime) : "Not sent"}</span>
                          </div>
                        </div>
                      )}
                      <div className="rpl-modal-actions">
                        <button className="btn btn-secondary" onClick={this.handleEditCancel} disabled={this.state.editSaveLoading}>Cancel</button>
                        <button className="btn btn-success" onClick={this.handleEditSave} disabled={this.state.editSaveLoading}>
                          {this.state.editSaveLoading ? <span><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span><span style={{ marginLeft: 8 }}>Saving...</span></span> : "Save"}
                        </button>
                      </div>
                    </React.Fragment>
                  )}
                </div>
              </div>
            )}

            {/* Confirmation Modal — review only, no edit fields */}
            {this.state.showConfirmModal && this.state.selectedPatient && (() => {
              const sp = this.state.selectedPatient;
              const durationLabel = (v) => {
                const n = Number(v);
                if (!n) return "—";
                const map = { 24:"1 day", 48:"2 days", 72:"3 days", 96:"4 days", 120:"5 days", 144:"6 days", 168:"7 days", 240:"10 days", 336:"14 days", 744:"31 days" };
                if (map[n]) return map[n];
                if (n < 24) return `${n} day${n !== 1 ? "s" : ""}`; // legacy: stored as days
                return `${Math.round(n / 24)} days`; // unknown hours → convert
              };
              return (
                <div className="rpl-modal-overlay">
                  <div className="rpl-modal">
                    <h5 className="rpl-modal-title">Confirm Billing</h5>
                    <p className="rpl-confirm-subtitle">Review the details below and confirm billing for this patient.</p>
                    <div className="rpl-confirm-summary">
                      <div className="rpl-cs-row"><span className="rpl-cs-key">Patient</span><span className="rpl-cs-val">{sp.name || "—"}</span></div>
                      <div className="rpl-cs-row"><span className="rpl-cs-key">Device ID</span><span className="rpl-cs-val">{sp.device_id || "—"}</span></div>
                      <div className="rpl-cs-row"><span className="rpl-cs-key">Customer</span><span className="rpl-cs-val">{sp.customerName || "—"}</span></div>
                      <div className="rpl-cs-row"><span className="rpl-cs-key">IP/OP ID</span><span className="rpl-cs-val">{sp.ip_op || "—"}</span></div>
                      <div className="rpl-cs-row"><span className="rpl-cs-key">Doctor</span><span className="rpl-cs-val">{this.normalizeDoctorName(sp.updatedDotorName && sp.updatedDotorName !== "NA" ? sp.updatedDotorName : sp.doctorname) || "—"}</span></div>
                      <div className="rpl-cs-row"><span className="rpl-cs-key">Duration</span><span className="rpl-cs-val">{durationLabel(sp.exp_device_use_days)}</span></div>
                      <div className="rpl-cs-row"><span className="rpl-cs-key">Hospital</span><span className="rpl-cs-val">{sp.hospital || "—"}</span></div>
                    </div>
                    <div className="rpl-modal-section-title">Report Delivery</div>
                    <div className="rpl-modal-delivery-status">
                      <div className={`rpl-ds-item ${sp.mailsentime ? "rpl-ds-sent" : "rpl-ds-pending"}`}>
                        <span className="rpl-ds-icon">✉</span>
                        <span className="rpl-ds-label">Email</span>
                        <span className="rpl-ds-val">{sp.mailsentime ? this.formatShortDate(sp.mailsentime) : "Not sent"}</span>
                      </div>
                      <div className={`rpl-ds-item ${sp.whatsappsenttime ? "rpl-ds-wa" : "rpl-ds-pending"}`}>
                        <span className="rpl-ds-icon">📱</span>
                        <span className="rpl-ds-label">WhatsApp</span>
                        <span className="rpl-ds-val">{sp.whatsappsenttime ? this.formatShortDate(sp.whatsappsenttime) : "Not sent"}</span>
                      </div>
                    </div>
                    <div className="rpl-modal-actions">
                      <button className="btn btn-secondary" onClick={this.handleConfirmCancel} disabled={this.state.confirmSaveLoading}>Cancel</button>
                      <button className="btn btn-success" onClick={this.handleConfirmSubmit} disabled={this.state.confirmSaveLoading}>
                        {this.state.confirmSaveLoading
                          ? <span><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span><span style={{ marginLeft: 8 }}>Confirming...</span></span>
                          : "Confirm Billing"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ReportPatientsList;